import * as _ from 'lodash';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { DisplayDecorator, JsonDisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { MeasurementToolsService } from '../../services/measurement-tools/measurement-tools.service';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { CompositeModel } from '../complex/composite.model';
import { DropdownModel } from '../complex/dropdown.model';
import { DateModelOptions } from '../options/date-model-options';
import { TextModelOptions } from '../options/text-model-options';
import { DateModel } from '../primitive/date.model';
import { JsonTextModel, TextModel } from '../primitive/text.model';
import { MeasurementToolsDetailsModel } from './measurement-tools-details.model';

export class CalibrationModel {
    public constructor(
        public toolDescription = '',
        public calibrationDate = '',
        public errorMessage = '',
    ) {}

    public static fromODataCalibrationDate(data: ODataCalibrationModel): CalibrationModel {
        return new CalibrationModel(data.Description, data.CalibrationDate, data.ErrorMessage);
    }
}

export class MeasurementToolModel extends CompositeModel {
    public measurementToolKey: string;
    public measurementToolId: TextModel;
    public calibration: CalibrationModel;
    public isHidden: boolean;

    public technicalId: TextModel;
    public textDescription: TextModel;
    public calibrationUntil: DateModel;
    public equipmentDescription: TextModel;
    public gid: TextModel;
    public showMeasurementDetails = false;
    public dropdownVisibilitySubject: BehaviorSubject<boolean>;
    public dropdownVisibility$: Observable<boolean>;

    public dropdownValues: DropdownModel<TextModel>;
    private readonly measurementToolsService: MeasurementToolsService;
    public constructor(technicalID = '', isHidden = false, dropDownEntries: Array<DisplayDecorator<TextModel>> = []) {
        super('MeasurementToolModel');
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const configService = injector.get(ConfigService);
        this.showMeasurementDetails = configService.getShowMeasurementToolsDetails();
        this.measurementToolsService = injector.get(MeasurementToolsService);

        this.measurementToolId = <TextModel>this.set(new TextModel('MeasuringTool', technicalID));
        modelOptionsLoader.load(this.measurementToolId, 'MeasurementTools', 'ID');
        this.isHidden = isHidden;
        if (configService.getShowCalibrationDate()) {
            this.calibration = new CalibrationModel();
        }
        this.dropdownVisibilitySubject = new BehaviorSubject<boolean>(dropDownEntries.length > 0);
        this.dropdownVisibility$ = this.dropdownVisibilitySubject.asObservable();
        if (this.showMeasurementDetails) {
            const num1 = 1000000;
            const num2 = 10000;
            this.measurementToolKey = `${moment.now()}-${Math.random() * (num1 - num2)}10000`;

            this.technicalId = new TextModel('TechIdentNo');
            modelOptionsLoader.load(this.technicalId, 'MeasurementTools', 'TechnicalId');
            this.equipmentDescription = new TextModel('Description');

            const dateOptions = new DateModelOptions({ readonly: true });
            this.calibrationUntil = new DateModel('CalibratedDate', moment.invalid(), dateOptions);

            const options = new TextModelOptions({ readonly: true });
            this.gid = new TextModel('UserID', '', options);

            this.textDescription = new TextModel('Description');
            modelOptionsLoader.load(this.textDescription, 'MeasurementTools', 'Description');

            this.dropdownValues = new DropdownModel<TextModel>('description', dropDownEntries);
            this.dropdownValues = assignModelOptions(
                this.dropdownValues,
                {
                    ...this.dropdownValues.options,
                    autoSelect: false,
                    required: true,
                },
                this,
            );
        }
    }

    private getToolDetailsByTechId(technicalID: string): MeasurementToolsDetailsModel | undefined {
        return this.measurementToolsService
            .getMeasurementToolsDetailsList()
            .find((tool) => tool.technicalId.toLocaleLowerCase() === technicalID.trim().toLocaleLowerCase());
    }

    public updateFields(): void {
        const technicalID = this.dropdownValues.selectedEntry
            ? this.dropdownValues.selectedEntry.model.value
            : this.technicalId.value;

        const existingTool = technicalID ? this.getToolDetailsByTechId(technicalID) : undefined;
        const toolDetails = existingTool ? existingTool : new MeasurementToolsDetailsModel(technicalID);

        this.calibrationUntil.value = toolDetails.calibratedDate;
        this.gid.value = toolDetails.gid;
        this.technicalId.value = toolDetails.technicalId;
        this.equipmentDescription.value = toolDetails.descriptionDropdownText;
        this.setDropdownVisibility(this.dropdownValues.value.length > 0);
        // The above gid and technicalId will not bind because text input component expect model to be change not properties
        // So we need to do clone deep again so that object reference changes and ngOnchange in Text component detects
        this.gid = _.cloneDeep(this.gid);
        this.calibrationUntil = _.cloneDeep(this.calibrationUntil);
        this.technicalId = _.cloneDeep(this.technicalId);
    }

    /**
     * Select the tool from the dropdown by Index
     *
     * @param index the index of the tool in the dropdown
     */
    public selectToolByIndex(index: number): void {
        this.dropdownValues.selectByIndex(index);
        this.setDropdownVisibility(true);
        this.textDescription.value = '';
        this.updateFields();
    }

    public setDropdownVisibility(value: boolean): void {
        this.dropdownVisibilitySubject.next(value);
    }

    public addToolToDropdown(tool: DisplayDecorator<TextModel> | undefined): void {
        if (tool) {
            this.dropdownValues.addModel(tool);
            this.measurementToolsService.sortByGid(this.dropdownValues.value);
        }
    }

    public removeToolFromDropdown(tool: string): void {
        const unSelectedToolList = this.dropdownValues.value.filter(
            (item) => item.model.value.toLocaleLowerCase() !== tool.trim().toLocaleLowerCase(),
        );
        this.dropdownValues.value = _.cloneDeep(unSelectedToolList);
    }

    public getUnselectedTools(): Array<DisplayDecorator<TextModel>> {
        const currentSelectedEntry = this.dropdownValues.selectedEntry?.model.value;

        if (currentSelectedEntry) {
            return this.dropdownValues.value.filter((item) => item.model.value !== currentSelectedEntry);
        }
        return this.dropdownValues.value;
    }

    /**
     * check if the tool exist in current dropdown model
     *
     * @param tool the tool to check
     * @returns true if the tool exist in the dropdown model
     */
    public isToolAvailableInDropdown(tool: string): boolean {
        return this.dropdownValues.value.some((value) => value.model.value === tool);
    }

    /**
     * Reset measurement tool dropdown
     */
    public reset(): void {
        this.dropdownValues.deselect();
        this.calibrationUntil.value = moment.invalid();
        this.gid.value = '';
        this.textDescription.value = '';

        this.gid = _.cloneDeep(this.gid);
        this.textDescription = _.cloneDeep(this.textDescription);
    }

    public toJSON(): unknown {
        return {
            isHidden: this.isHidden,
            calibration: this.calibration ? this.calibration : '',
            measurementToolId: this.measurementToolId,
            selectedEquipment: this.dropdownValues?.selectedEntry?.model.value,
            gid: this.gid?.value,
            technicalId: this.technicalId?.value,
            calibrationUntil: this.calibrationUntil?.value ? this.calibrationUntil.value : moment.invalid(),
            textDescription: this.textDescription?.value,
            showDropdown: this.dropdownVisibilitySubject.value,
            dropdownEntries: this.dropdownValues?.value,
            measurementToolKey: this.measurementToolKey,
        };
    }

    public onDescriptionTextChange(): void {
        this.equipmentDescription.value = this.textDescription.value;
    }

    public getOutModel(): ODataMeasurementToolOutModel {
        const outModel = <ODataMeasurementToolOutModel>super.getOutModel();

        if (this.showMeasurementDetails) {
            outModel.MeasuringTool = this.technicalId.value;
            outModel.MeasuringToolDesc = this.equipmentDescription.value;
        } else {
            outModel.MeasuringTool = this.measurementToolId.value;
        }
        return outModel;
    }
}

export class MeasurementToolModelFromJson extends MeasurementToolModel {
    public constructor(data: JsonMeasurementToolModel) {
        super(
            data.measurementToolId?.value,
            data.isHidden,
            data.dropdownEntries
                ? data.dropdownEntries.map(
                      (v) => new DisplayDecorator(v.displayName, new TextModel(v.model.name, <string>v.model.value)),
                  )
                : undefined,
        );
        if (data.calibration) {
            this.calibration.toolDescription = data.calibration.toolDescription ?? '';
            this.calibration.calibrationDate = data.calibration.calibrationDate ?? '';
            this.measurementToolId = assignModelOptions(this.measurementToolId, {
                ...this.measurementToolId.options,
                readonly: data.measurementToolId.options.readonly,
            });
        }
        if (this.showMeasurementDetails) {
            this.measurementToolKey = data.measurementToolKey;
            if (data.selectedEquipment && !data.textDescription && this.dropdownValues?.value.length) {
                this.dropdownValues.selectByValue(data.selectedEquipment);
                this.updateFields();
                return;
            }
            this.technicalId.value = data.measurementToolId?.value
                ? data.measurementToolId.value
                : data.technicalId
                  ? data.technicalId
                  : '';
            this.textDescription.value = data.textDescription;
            this.gid.value = data.gid;
            this.calibrationUntil.value = data.calibrationUntil ? moment(data.calibrationUntil) : moment.invalid();
            this.updateFields();
            this.onDescriptionTextChange();
        }
    }
}

export interface JsonMeasurementToolModel {
    measurementToolKey: string;
    measurementToolId: JsonTextModel;
    isHidden: boolean;
    calibration?: CalibrationModel;
    selectedEquipment: string;
    textDescription: string;
    showDropdown: boolean;
    gid: string;
    calibrationUntil: moment.Moment;
    technicalId: string;
    dropdownEntries: JsonDisplayDecorator[];
    // This is only for migration, can be removed after wave2 and wave3 go live
    initialDropdownEntries: JsonMeasurementToolsDetailsModel[];
}

export interface ODataMeasurementToolOutModel extends ODataObject {
    MeasuringTool: string;
    MeasuringToolDesc: string;
}

export interface ODataCalibrationModel extends ODataObject {
    __metadata: {
        id: string;
        uri: string;
        type: string;
    };
    Equipment: string;
    ErrorMessage: string;
    Description: string;
    CalibrationDate: string;
}

export interface JsonCalibrationModel {
    toolDescription: string;
    calibrationDate: string;
    errorMessage: string;
}
export interface JsonMeasurementToolsDetailsModel {
    technicalId: string;
    descriptionText: string;
    descriptionDropdownText: string;
    calibrationUntil: string;
    gid: string;
    isSelected: boolean;
    message: string;
}
